import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CsrAssuranceProvider } from '../../../models/csrAssuranceProvider';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent } from '../../shared/lookup-table/lookup-table.component';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-csr-assurance-provider-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    NgxSpinnerModule,
    LookupTableSearchBoxComponent
  ],
  templateUrl: './csr-assurance-provider-list.component.html',
  styleUrl: './csr-assurance-provider-list.component.scss'
})
export class CsrAssuranceProviderListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  csrAssuranceProviders = signal<CsrAssuranceProvider[]>([]);
  allCsrAssuranceProviders: CsrAssuranceProvider[] = [];
  rightItem: CsrAssuranceProvider | undefined = undefined;
  matchedItems: CsrAssuranceProvider[] | undefined = undefined;
  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadCsrAssuranceProviders();
  }

  loadCsrAssuranceProviders(): void {
    this.spinnerService.show();
    this.lutService.getCsrAssuranceProviders().subscribe({
      next: (data) => {
        // Sort alphabetically by name before setting
        const sortedData = this.sortCsrAssuranceProviders(data);
        this.allCsrAssuranceProviders = sortedData;
        this.csrAssuranceProviders.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading ESG assurance providers:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortCsrAssuranceProviders(csrAssuranceProviders: CsrAssuranceProvider[]): CsrAssuranceProvider[] {
    return [...csrAssuranceProviders].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }

  onCreateCsrAssuranceProvider(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(csrAssuranceProvider: CsrAssuranceProvider): void {
  }

  onEditCsrAssuranceProvider(csrAssuranceProvider: CsrAssuranceProvider): void {
  }

  onDeleteCsrAssuranceProvider(csrAssuranceProvider: CsrAssuranceProvider): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(csrAssuranceProvider.name)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalCsrAssuranceProviders = this.csrAssuranceProviders();
          const originalAllCsrAssuranceProviders = this.allCsrAssuranceProviders;

          // 1. Optimistically remove from local state immediately
          this.csrAssuranceProviders.update(csrAssuranceProviders => csrAssuranceProviders.filter(cap => cap.id !== csrAssuranceProvider.id));
          this.allCsrAssuranceProviders = this.allCsrAssuranceProviders.filter(cap => cap.id !== csrAssuranceProvider.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteCsrAssuranceProvider(csrAssuranceProvider.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('ESG assurance provider deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting ESG assurance provider:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.csrAssuranceProviders.set(originalCsrAssuranceProviders);
              this.allCsrAssuranceProviders = originalAllCsrAssuranceProviders;
              this.toast.error(`<strong>Failed to delete ESG Assurance Provider:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: CsrAssuranceProvider, newName: string} | {item: CsrAssuranceProvider, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };
    const originalAllCsrAssuranceProviders = this.allCsrAssuranceProviders;

    // Prepare updated item
    let updatedItem: CsrAssuranceProvider;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.csrAssuranceProviders.update(csrAssuranceProviders => {
      const updated = csrAssuranceProviders.map(cap => cap.id === event.item.id ? updatedItem : cap);
      return this.sortCsrAssuranceProviders(updated);
    });
    this.allCsrAssuranceProviders = this.sortCsrAssuranceProviders(
      this.allCsrAssuranceProviders.map(cap => cap.id === event.item.id ? updatedItem : cap)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateCsrAssuranceProvider(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG assurance provider updated successfully');
        // 3. On success: Update with server response and re-sort to maintain alphabetical order
        this.csrAssuranceProviders.update(csrAssuranceProviders => {
          const updated = csrAssuranceProviders.map(cap => cap.id === event.item.id ? response : cap);
          return this.sortCsrAssuranceProviders(updated);
        });
        this.allCsrAssuranceProviders = this.sortCsrAssuranceProviders(
          this.allCsrAssuranceProviders.map(cap => cap.id === event.item.id ? response : cap)
        );
      },
      error: (error) => {
        console.error('Error updating ESG assurance provider:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.csrAssuranceProviders.update(csrAssuranceProviders =>
          csrAssuranceProviders.map(cap => cap.id === event.item.id ? originalItem : cap)
        );
        this.allCsrAssuranceProviders = originalAllCsrAssuranceProviders;
        this.toast.error(`<strong>Failed to update ESG Assurance Provider:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const newCsrAssuranceProvider: CsrAssuranceProvider = { id: tempId, name: event['name'], systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.csrAssuranceProviders.update(csrAssuranceProviders => this.sortCsrAssuranceProviders([...csrAssuranceProviders, newCsrAssuranceProvider]));
    this.allCsrAssuranceProviders = this.sortCsrAssuranceProviders([...this.allCsrAssuranceProviders, newCsrAssuranceProvider]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createCsrAssuranceProvider({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('ESG assurance provider created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain alphabetical order
        this.csrAssuranceProviders.update(csrAssuranceProviders => {
          const updated = csrAssuranceProviders.map(cap => cap.id === tempId ? response : cap);
          return this.sortCsrAssuranceProviders(updated);
        });
        this.allCsrAssuranceProviders = this.sortCsrAssuranceProviders(
          this.allCsrAssuranceProviders.map(cap => cap.id === tempId ? response : cap)
        );
      },
      error: (error) => {
        console.error('Error creating ESG assurance provider:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.csrAssuranceProviders.update(csrAssuranceProviders =>
          csrAssuranceProviders.filter(cap => cap.id !== tempId)
        );
        this.allCsrAssuranceProviders = this.allCsrAssuranceProviders.filter(cap => cap.id !== tempId);
        this.toast.error(`<strong>Failed to create ESG Assurance Provider:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: CsrAssuranceProvider, matchedItems?: CsrAssuranceProvider[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onCsrAssuranceProviderCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog
    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalCsrAssuranceProviders = this.csrAssuranceProviders();

        // 1. Optimistically remove merged items from local state
        this.csrAssuranceProviders.update(csrAssuranceProviders =>
          csrAssuranceProviders.filter(cap => !bulkMergeRequest.leftIds.includes(cap.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeCsrAssuranceProvidersBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('ESG assurance providers merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadCsrAssuranceProviders();
          },
          error: (error) => {
            console.error('Error merging ESG assurance providers:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.csrAssuranceProviders.set(originalCsrAssuranceProviders);
            // Restore selection state for retry
            this.rightItem = originalCsrAssuranceProviders.find(cap => cap.id === bulkMergeRequest.rightId);
            this.matchedItems = originalCsrAssuranceProviders.filter(cap => bulkMergeRequest.leftIds.includes(cap.id));
            this.toast.error(`<strong>Failed to merge ESG Assurance Providers:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.spinnerService.show();
    setTimeout(() => {
      if (searchFilter.name) {
        const filtered = this.allCsrAssuranceProviders.filter(item =>
          item.name.toLowerCase().includes(searchFilter.name?.toLowerCase() ?? '')
        );
        this.csrAssuranceProviders.set(filtered);
      } else {
        this.csrAssuranceProviders.set(this.allCsrAssuranceProviders);
      }
      this.spinnerService.hide();
    }, 1500);
  }
}
